import { Component, h, Prop, State, Event, EventEmitter } from '@stencil/core';
import { DateUtils, FeedItemSubType, FeedItemType, FileDoc, FileType, PersonDoc, SubmittalDoc, SubmittalFeedItemDoc, SubmittalRevision, WorkflowState } from 'common';
import { CommentSubmit } from '../../global/models/CommentSubmit';
import { ConcourseDb, FeedService, User } from '../../global';
import { SubmittalUtils } from '..';

@Component({
  tag: 'submittal-comments-card',
  styleUrls: ['../../global/styles/workflow-view.css'],
})
export class SubmittalCommentsCard {
  @Prop() projectId: string;
  @Prop() submittalId: string;
  @Prop() submittal: SubmittalDoc;
  @Prop() people: PersonDoc[] = [];
  @Prop() files: FileDoc[] = [];
  @Prop() feed: SubmittalFeedItemDoc[] = [];
  @Prop() selectedRevisionIndex: number = -1;
  @Prop() canWrite: boolean = false;
  @Prop() isMobile: boolean = false;
  @Prop() activeFilter: string = 'all';
  @Prop() newComment: string = '';

  @Event() setFilter: EventEmitter<string>;
  @Event() commentSubmit: EventEmitter<CommentSubmit>;
  @Event() newCommentChange: EventEmitter<string>;
  @Event() feedSelectedChange: EventEmitter<SubmittalFeedItemDoc[]>;
  @Event() handleFinishSubmittalMarkup: EventEmitter<string>;
  @Event() setCommentsRef: EventEmitter<HTMLDivElement>;
  @Event() saveSubmittal: EventEmitter<void>;

  @State() feedSelected: SubmittalFeedItemDoc[] = [];

  private commentsRef: HTMLDivElement;

  get user(): User {
    return globalThis.user;
  }

  get latestRevision(): SubmittalRevision {
    return this.submittal.revisions[this.submittal.revisions.length - 1];
  }

  get revision(): SubmittalRevision {
    if (this.selectedRevisionIndex === -1) {
      return this.latestRevision;
    }
    return this.submittal.revisions[this.selectedRevisionIndex];
  }

  get currentState(): WorkflowState {
    return SubmittalUtils.getRevisionState(this.submittal, this.revision);
  }

  componentDidLoad() {
    if (this.commentsRef) {
      this.setCommentsRef.emit(this.commentsRef);
    }
  }

  getFilterMapping() {
    return {
      'all': null,
      'comments': [FeedItemSubType.COMMENT, FeedItemSubType.EDIT_COMMENT],
      'files': [FeedItemSubType.FILE, FeedItemSubType.PHOTO, FeedItemSubType.DRAWING, FeedItemSubType.SPECIFICATION, FeedItemSubType.MARKED],
      'status-history': [FeedItemSubType.STATUS, FeedItemSubType.PROGRESS, FeedItemSubType.APPROVE, FeedItemSubType.REJECT, FeedItemSubType.PROPOSE],
      'accepted-answers': 'accepted-answers',
    };
  }

  getFilteredFeed(): SubmittalFeedItemDoc[] {
    if (this.activeFilter === 'all') {
      return this.feed;
    }

    const nonDeletedFeed = this.feed.filter(item => !item.deleted_by);
    const filterMapping = this.getFilterMapping();
    const allowedSubTypes = filterMapping[this.activeFilter];

    if (!allowedSubTypes) {
      return nonDeletedFeed;
    }

    if (this.activeFilter === 'accepted-answers') {
      return nonDeletedFeed.filter(item => this.revision.accepted_answers && this.revision.accepted_answers.includes(item._id));
    }

    return nonDeletedFeed.filter(item => allowedSubTypes.includes(item.sub));
  }

  getFeedItemButton(item: SubmittalFeedItemDoc) {
    return (
      <feed-item-accept-button
        item={item}
        canWrite={this.canWrite}
        currentStateId={this.currentState?.id}
        currentStateDueBy={this.currentState?.dueBy || []}
        acceptedAnswers={this.revision.accepted_answers || []}
        projectId={this.projectId}
        onAcceptAnswer={e => this.setAcceptedAnswer(e.detail.answerId, e.detail.text)}
        onDeleteItem={e => this.deleteFeedItem(e.detail as SubmittalFeedItemDoc)}
      />
    );
  }

  async setAcceptedAnswer(answerId: string, text: string) {
    const isAccepted = this.revision.accepted_answers?.includes(answerId);
    if (isAccepted) {
      this.revision.accepted_answers = this.revision.accepted_answers.filter(id => id !== answerId);
    } else {
      this.revision.accepted_answers = [...(this.revision.accepted_answers || []), answerId];
    }

    const feedItem: SubmittalFeedItemDoc = {
      _id: ConcourseDb.makeShortDocId(),
      user_id: this.user.id,
      date: DateUtils.currentIso,
      type: FeedItemType.SUBMITTAL,
      related_id: this.submittalId,
      revision: this.revision.num,
      sub: isAccepted ? FeedItemSubType.REJECT : FeedItemSubType.ACCEPT,
      sub_id: answerId,
      text: isAccepted ? 'rejected an answer' : 'accepted an answer',
    };
    await FeedService.storeFeedItem(this.projectId, feedItem);
    this.saveSubmittal.emit();
  }

  async deleteFeedItem(item: SubmittalFeedItemDoc) {
    const updatedItem = { ...item, deleted_by: this.user.id, deleted_date: DateUtils.currentIso };
    await FeedService.storeFeedItem(this.projectId, updatedItem);
  }

  render() {
    const filteredFeed = this.getFilteredFeed();
    return (
      <div class="ion-display-flex ion-flex-column" style={{ height: '100%' }}>
        <workflow-view-filter-buttons
          activeFilter={this.activeFilter}
          acceptedAnswersCount={this.revision.accepted_answers?.length || 0}
          isMobile={this.isMobile}
          onFilterChange={e => this.setFilter.emit(e.detail)}
        />
        <div class="flex-grow cc-scrollbars workflow-view-detail-feed-container ion-padding-horizontal" ref={el => (this.commentsRef = el as HTMLDivElement)}>
          {filteredFeed.length === 0 ? (
            <p>No items found for the selected filter.</p>
          ) : (
            <concourse-feed
              projectId={this.projectId}
              people={this.people}
              files={this.files}
              feed={filteredFeed}
              onItemsSelected={e => {
                this.feedSelected = e.detail as SubmittalFeedItemDoc[];
                this.feedSelectedChange.emit(this.feedSelected);
              }}
              buttonCallback={this.isMobile ? null : this.getFeedItemButton.bind(this)}
              notifyMarkup={(fileId: string) => this.handleFinishSubmittalMarkup.emit(fileId)}
            />
          )}
        </div>
        {this.canWrite && (
          <div class="ion-padding-horizontal ion-padding-bottom">
            {this.feedSelected.length > 0 ? (
              <div class="ion-display-flex ion-align-items-center" style={{ height: '50px' }}>
                <span style={{ color: '#666', flex: '1' }}>{this.feedSelected.length} comments selected</span>
                <ion-button fill="clear" onClick={() => console.log(`Delete selected items: ${this.feedSelected.map(item => item._id).join(', ')}`)}>
                  <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                </ion-button>
              </div>
            ) : (
              <concourse-comment
                projectId={this.projectId}
                feature="submittals"
                featureId={this.submittalId}
                placeholder="Enter comment"
                rows={`${this.isMobile ? 1 : 3}`}
                isMobile={this.isMobile}
                value={this.newComment}
                fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittalId)}
                fileType={FileType.DOCUMENT}
                onMessageChange={(e: CustomEvent) => this.newCommentChange.emit(e.detail)}
                onCommentSubmit={(e: CustomEvent<CommentSubmit>) => this.commentSubmit.emit(e.detail)}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}
